import IconChevronDown from '@apollo/icons/small/IconChevronDown.svg';
import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import Config from 'src/lib/config';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { GraphRef } from '../../hooks/useGraphRef';

export const ProposeChangesButton = React.forwardRef(
  (
    {
      className,
      disabled,
      tooltipPlacement,
      onClickMenuButton,
      shouldShowUpdateWithRover,
      graphRef,
      warningLabel,
      ...rest
    }: {
      className?: string;
      disabled: boolean;
      tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
      onClickMenuButton?: () => void;
      shouldShowUpdateWithRover?: boolean;
      graphRef?: GraphRef;
      warningLabel?: string | null;
    } & Omit<
      React.DetailedHTMLProps<
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        HTMLButtonElement
      >,
      'ref' | 'color'
    >,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const location = useLocation();

    return (
      <Tooltip
        label={
          warningLabel ??
          'You are unable to create new proposals as a Consumer. Proposals cannot yet be made from editing the API schema, only subgraph schemas, and your role does not include access to the subgraph schemas.'
        }
        disabled={!disabled}
        shouldWrapChildren={disabled}
        placement={tooltipPlacement}
      >
        <ButtonGroup className={className} isAttached>
          <Button
            variant="primary"
            isDisabled={disabled}
            as={Link}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.proposeSchemaChanges,
            })}
            {...rest}
          >
            Propose changes
          </Button>
          <Menu placement="bottom-end" autoSelect={false}>
            <MenuButton
              isDisabled={disabled}
              as={IconButton}
              variant="primary"
              ref={ref}
              onClick={onClickMenuButton}
            >
              <IconChevronDown />
            </MenuButton>
            <MenuContent>
              <MenuList>
                <MenuItem
                  as={Link}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.proposeSchemaChanges,
                    additionalSearchParameters: {
                      shouldPromptForNewSubgraphName: true,
                    },
                  })}
                >
                  <span className="text-sm">Propose a new subgraph</span>
                </MenuItem>
                {shouldShowUpdateWithRover && (
                  <PermissionWarning
                    graphId={graphRef?.graphId}
                    graphVariant={graphRef?.graphVariant}
                    graphVariantPermissions="canPushSchemas"
                    placement="left"
                    childRenderer={(hasPermission) => (
                      <MenuItem
                        isDisabled={!hasPermission}
                        as={Link}
                        to={setOverlayVisibleLocation({
                          location,
                          overlayName: Config.modals.publishYourSchema,
                          additionalSearchParameters: {
                            [Config.queryParameters.PublishSchemaUpdate]:
                              'true',
                          },
                        })}
                      >
                        Update schema using Rover
                      </MenuItem>
                    )}
                  />
                )}
              </MenuList>
            </MenuContent>
          </Menu>
        </ButtonGroup>
      </Tooltip>
    );
  },
);
